/* ============================================================================
   JMT Variant Sync v4.5 — PDP controls ↔ Pref (experimental-cut isolation)
   - Applies JMT_PREF → PDP by matching existing option values/text
   - Captures PDP user changes → JMT_PREF (engine persists + emits)
   - Prevents feedback loops
   - Debounced rebind/reapply on dynamic re-renders
   - Supports native selects and radio/pill variant controls
   - Supports Cut+Size products AND Size-only products
============================================================================ */

(function () {
  'use strict';

  var LANDING_VIEW = '';
  try {
    LANDING_VIEW = new URL(window.location.href).searchParams.get('view') || '';
  } catch (error) {}

  function preserveLandingView() {
    if (!LANDING_VIEW) return;

    function restore() {
      try {
        var url = new URL(window.location.href);
        if (url.searchParams.get('view') === LANDING_VIEW) return;
        url.searchParams.set('view', LANDING_VIEW);
        window.history.replaceState(
          window.history.state,
          '',
          url.pathname + url.search + url.hash
        );
      } catch (error) {}
    }

    [0, 160, 800, 2200].forEach(function (delay) {
      window.setTimeout(restore, delay);
    });
  }

  // Theme sections can stream their scripts at slightly different times.
  // Wait for the preference engine instead of permanently exiting on a race.
  var PREF_EVT = 'jmt:pref:change';
  var STARTED = false;

  var APPLYING = false;
  var CAPTURING_PDP = false;
  var ACTIVE_EXPERIMENTAL_CUT = window.__JMT_ACTIVE_EXPERIMENTAL_CUT__ || '';
  var REBIND_TIMER = null;
  var OBSERVING = false;

  function ready(fn) {
    document.readyState === 'loading'
      ? document.addEventListener('DOMContentLoaded', fn)
      : fn();
  }

  function norm(s) {
    return String(s || '').toLowerCase().trim();
  }

  function normalizeCut(s) {
    var t = String(s || '').trim();
    if (!t) return '';
    return t.replace(/\s+cut$/i, '').trim(); // "Slim Cut" -> "Slim"
  }
  var STANDARD_CUTS = {
    'classic': true,
    'slim': true,
    'tower': true,
    'tear drop': true,
    'teardrop': true
  };

  function isStandardCut(value) {
    var key = normalizeCut(value).replace(/\s+/g, ' ').toLowerCase();
    return !!STANDARD_CUTS[key];
  }
  function setActiveExperimentalCut(value) {
    ACTIVE_EXPERIMENTAL_CUT = normalizeCut(value);
    window.__JMT_ACTIVE_EXPERIMENTAL_CUT__ = ACTIVE_EXPERIMENTAL_CUT;
  }

  function rememberExperimentalCut(event) {
    var control = event && event.target;
    if (!control) return;

    var type = control.tagName === 'SELECT' ? inferType(control) :
      (control.matches && control.matches('input[type="radio"]') ? inferRadioType(control) : '');
    if (type !== 'cut') return;

    var value = control.value;
    if (isPlaceholderValue(value)) return;
    if (!isStandardCut(value)) {
      setActiveExperimentalCut(value);
    } else if (event.isTrusted) {
      setActiveExperimentalCut('');
    }
  }

  function normalizeSize(s) {
    var t = String(s || '').trim();
    if (!t) return '';
    return t.split(/\s+/)[0].trim(); // "XL 34–36" -> "XL"
  }

  function isPlaceholderValue(v) {
    var s = String(v || '').trim();
    if (!s) return true;
    var n = s.toLowerCase();
    return n === 'choose' || n === 'select' || n === 'cut' || n === 'size' || n === 'choose cut' || n === 'choose size';
  }

  function isLikelyVariantSelect(sel) {
    if (!sel || sel.tagName !== 'SELECT') return false;
    if (sel.hasAttribute('data-jmt-product-adjustment')) return false;

    var name = norm(sel.name);
    var id = norm(sel.id);

    // Common Shopify patterns
    if (name.includes('options[') || name.includes('option')) return true;
    if (id.includes('option') || id.includes('single-option') || id.includes('option-')) return true;

    // Fallback: variant selects usually have multiple choices
    if (sel.options && sel.options.length >= 2) return true;

    return false;
  }

  function getVariantSelects() {
    // Keep scope tight to product area + variant-selects
    var nodes = Array.from(
      document.querySelectorAll(
        'variant-selects select, .product__info-container select, product-info select'
      )
    );
    return nodes.filter(isLikelyVariantSelect);
  }

  function isLikelyVariantRadio(radio) {
    if (!radio || radio.tagName !== 'INPUT' || radio.type !== 'radio') return false;
    if (radio.hasAttribute('data-jmt-product-adjustment')) return false;
    return !!radio.closest('variant-selects, variant-radios, product-info');
  }

  function getVariantRadios() {
    return Array.from(
      document.querySelectorAll(
        'variant-selects input[type="radio"], variant-radios input[type="radio"], product-info input[type="radio"]'
      )
    ).filter(isLikelyVariantRadio);
  }

  function radioOptionLabel(radio) {
    var name = String((radio && radio.name) || '').trim().replace(/-\d+\s*$/, '');
    if (name) return name;

    var fieldset = radio && radio.closest ? radio.closest('fieldset') : null;
    var legend = fieldset ? fieldset.querySelector('legend') : null;
    return String((legend && legend.textContent) || '').replace(/^\s*\d+\s*/, '').trim();
  }

  function inferRadioType(radio) {
    var optionLabel = radioOptionLabel(radio);
    var name = norm(optionLabel);
    var id = norm((radio && radio.id) || '');

    if (window.JMT_VARIANTS) {
      var namedRole = window.JMT_VARIANTS.role(optionLabel);
      if (namedRole === 'pouchStyle') return 'cut';
      if (namedRole === 'waistSize') return 'size';
      if (namedRole === 'thongStrapStyle') return 'thongStrapStyle';
    }

    if (name.includes('strap') || name.includes('back') || id.includes('strap')) return 'thongStrapStyle';
    if (name.includes('pouch') || name.includes('cut') || id.includes('pouch') || id.includes('cut')) return 'cut';
    if (name.includes('waist') || name.includes('size') || id.includes('size')) return 'size';

    return '';
  }

  function groupVariantRadios() {
    var groups = {};
    var radios = getVariantRadios();

    for (var i = 0; i < radios.length; i++) {
      var radio = radios[i];
      var fieldset = radio.closest ? radio.closest('fieldset') : null;
      var key = String(radio.name || '').trim() || (fieldset && fieldset.id) || ('radio-group-' + i);
      if (!groups[key]) groups[key] = [];
      groups[key].push(radio);
    }

    return Object.keys(groups).map(function (key) { return groups[key]; });
  }

  function radioOptionIndex(radio) {
    var name = String((radio && radio.name) || '').trim();
    var match = name.match(/-(\d+)\s*$/);
    if (match) return Math.max(0, parseInt(match[1], 10) - 1);

    var fieldset = radio && radio.closest ? radio.closest('fieldset') : null;
    var picker = radio && radio.closest ? radio.closest('variant-selects, variant-radios') : null;
    if (!fieldset || !picker) return -1;

    return Array.from(picker.querySelectorAll('fieldset')).indexOf(fieldset);
  }

  function getSitewideSelectForRadio(radio) {
    var optionIndex = radioOptionIndex(radio);
    if (optionIndex < 0) return null;

    return document.querySelector(
      '.jmt-sticky__select[data-option-index="' + optionIndex + '"]'
    );
  }

  function syncRadioToSitewideControl(radio, force) {
    var select = getSitewideSelectForRadio(radio);
    if (!select) return false;

    var wanted = String((radio && radio.value) || '');
    var option = Array.from(select.options || []).find(function (item) {
      return item && item.value === wanted;
    });
    if (!option) return false;

    if (select.value !== option.value || force) {
      select.value = option.value;
      select.setAttribute('data-jmt-last-real-value', option.value);
      select.dispatchEvent(new Event('change', { bubbles: true }));
      preserveLandingView();
    }

    return true;
  }

  function inferType(select) {
    // Prefer name/id hints
    var name = norm(select.name);
    var id = norm(select.id);

    var optionLabel = name;
    var nameMatch = String(select.name || '').match(/^options\[(.*)\]$/i);
    if (nameMatch) optionLabel = nameMatch[1];
    if (window.JMT_VARIANTS) {
      var namedRole = window.JMT_VARIANTS.role(optionLabel);
      if (namedRole === 'pouchStyle') return 'cut';
      if (namedRole === 'waistSize') return 'size';
      if (namedRole === 'thongStrapStyle') return 'thongStrapStyle';
    }
    if (name.includes('strap') || id.includes('strap')) return 'thongStrapStyle';
    if (name.includes('pouch') || name.includes('cut') || id.includes('pouch') || id.includes('cut')) return 'cut';
    if (name.includes('size') || id.includes('size')) return 'size';

    // Fallback: inspect options
    var opts = Array.from(select.options || []);

    // Cut cues
    var hasCutWords = opts.some(function (o) {
      var s = norm((o && (o.value || o.textContent)) || '');
      return s.includes('slim') || s.includes('classic');
    });
    if (hasCutWords) return 'cut';

    // Size cues
    var hasSizePattern = opts.some(function (o) {
      var s = String((o && (o.value || o.textContent)) || '').trim();
      return (
        /^[sml]\b/i.test(s) ||
        /^[0-9]*x?l\b/i.test(s) ||
        /^2xl\b/i.test(s) || /^3xl\b/i.test(s) || /^4xl\b/i.test(s) ||
        /^5xl\b/i.test(s) || /^6xl\b/i.test(s) || /^7xl\b/i.test(s)
      );
    });
    if (hasSizePattern) return 'size';

    return '';
  }

  function matchAndSet(select, type, want) {
    if (!select || !want) return false;

    var wanted = type === 'cut' ? normalizeCut(want) :
      (type === 'size' ? normalizeSize(want) : String(want || '').trim());
    if (!wanted) return false;

    var opts = Array.from(select.options || []);
    if (!opts.length) return false;

    var found = null;

    for (var i = 0; i < opts.length; i++) {
      var opt = opts[i];
      if (!opt) continue;

      var v = opt.value || '';
      var t = opt.textContent || '';

      var nv = type === 'cut' ? normalizeCut(v) :
        (type === 'size' ? normalizeSize(v) : String(v || '').trim());
      var nt = type === 'cut' ? normalizeCut(t) :
        (type === 'size' ? normalizeSize(t) : String(t || '').trim());

      if (nv === wanted || nt === wanted) {
        found = opt;
        break;
      }
    }

    if (!found) return false;

    // Only change when needed
    if (select.value !== found.value) {
      select.value = found.value;

      // Trigger theme listeners (Spotlight/Dawn rely on this)
      select.dispatchEvent(new Event('change', { bubbles: true }));
    }

    return true;
  }

  function matchAndSetRadioGroup(radios, type, want) {
    if (!radios || !radios.length || !want) return false;

    var wanted = type === 'cut' ? normalizeCut(want) :
      (type === 'size' ? normalizeSize(want) : String(want || '').trim());
    if (!wanted) return false;

    var found = null;
    for (var i = 0; i < radios.length; i++) {
      var radio = radios[i];
      var raw = String((radio && radio.value) || '').trim();
      var normalized = type === 'cut' ? normalizeCut(raw) :
        (type === 'size' ? normalizeSize(raw) : raw);

      if (normalized === wanted) {
        found = radio;
        break;
      }
    }

    if (!found || found.disabled) return false;

    if (!found.checked) {
      found.checked = true;
      found.dispatchEvent(new Event('change', { bubbles: true }));
      preserveLandingView();
    }

    return true;
  }

  function applyTypeToControls(selects, radioGroups, type, want) {
    if (!want) return;

    for (var i = 0; i < selects.length; i++) {
      if (inferType(selects[i]) === type) matchAndSet(selects[i], type, want);
    }

    for (var j = 0; j < radioGroups.length; j++) {
      var group = radioGroups[j];
      if (
        group.length &&
        !getSitewideSelectForRadio(group[0]) &&
        inferRadioType(group[0]) === type
      ) {
        matchAndSetRadioGroup(group, type, want);
      }
    }
  }

  function normalizedValue(type, value) {
    if (type === 'cut') return normalizeCut(value);
    if (type === 'size') return normalizeSize(value);
    return String(value || '').trim();
  }

  function commitSitewideControlsIfNeeded(radioGroups, pref, preserveExperimentalCut) {
    for (var i = 0; i < radioGroups.length; i++) {
      var group = radioGroups[i];
      if (!group.length) continue;

      var type = inferRadioType(group[0]);
      if (preserveExperimentalCut && type === 'cut') continue;
      var want = type === 'cut' ? pref.cut :
        (type === 'size' ? pref.size :
          (type === 'thongStrapStyle' ? pref.thongStrapStyle : ''));
      if (!want) continue;

      var checked = group.find(function (radio) { return radio.checked; });
      if (checked && normalizedValue(type, checked.value) === normalizedValue(type, want)) continue;

      var sharedSelect = getSitewideSelectForRadio(group[0]);
      if (!sharedSelect) continue;

      // The shared controls already contain every saved option. One change
      // event commits their combined selection to the product form and URL.
      sharedSelect.dispatchEvent(new Event('change', { bubbles: true }));
      preserveLandingView();
      return true;
    }

    return false;
  }

  function currentExperimentalCut(selects, radioGroups) {
    for (var i = 0; i < selects.length; i++) {
      if (inferType(selects[i]) !== 'cut') continue;
      var selectValue = selects[i].value;
      if (!isPlaceholderValue(selectValue) && !isStandardCut(selectValue)) return normalizeCut(selectValue);
    }

    for (var j = 0; j < radioGroups.length; j++) {
      var group = radioGroups[j];
      if (!group.length || inferRadioType(group[0]) !== 'cut') continue;
      var checked = group.find(function (radio) { return radio.checked; });
      if (checked && !isPlaceholderValue(checked.value) && !isStandardCut(checked.value)) return normalizeCut(checked.value);
    }

    return '';
  }

  function applyPrefToPDP(pref, forceStandardCut) {
    if (!pref) return;

    var selects = getVariantSelects();
    var radioGroups = groupVariantRadios();
    if (!selects.length && !radioGroups.length) return;

    if (forceStandardCut) {
      setActiveExperimentalCut('');
    } else {
      var currentExperimental = currentExperimentalCut(selects, radioGroups);
      if (currentExperimental) setActiveExperimentalCut(currentExperimental);
    }

    var preserveExperimentalCut = !!ACTIVE_EXPERIMENTAL_CUT;
    var cutToApply = preserveExperimentalCut ? ACTIVE_EXPERIMENTAL_CUT : pref.cut;
    APPLYING = true;

    try {
      // Restore the PDP-only cut after Shopify rerenders another option.
      applyTypeToControls(selects, radioGroups, 'cut', cutToApply);
      applyTypeToControls(selects, radioGroups, 'size', pref.size);
      applyTypeToControls(selects, radioGroups, 'thongStrapStyle', pref.thongStrapStyle);
      commitSitewideControlsIfNeeded(radioGroups, pref, preserveExperimentalCut);
    } finally {
      APPLYING = false;
    }
  }

  function setCapturedPref(patch) {
    CAPTURING_PDP = true;
    try {
      return window.JMT_PREF.set(patch);
    } finally {
      CAPTURING_PDP = false;
    }
  }

  function bindCaptureOnce(select) {
    if (!select || select.__jmtBound) return;
    select.__jmtBound = true;

    select.addEventListener('change', function (event) {
      if (APPLYING || !window.JMT_PREF) return;

      var type = inferType(select);
      if (!type) return;

      var v = select.value;
      if (isPlaceholderValue(v)) return;

      // Capture raw; engine normalizes and emits
      if (type === 'cut') {
        if (!isStandardCut(v)) {
          setActiveExperimentalCut(v);
        } else if (event.isTrusted) {
          setActiveExperimentalCut('');
        }
        setCapturedPref({ cut: v });
      }
      if (type === 'size') setCapturedPref({ size: v });
      if (type === 'thongStrapStyle') setCapturedPref({ thongStrapStyle: v });
    });
  }

  function bindRadioCaptureOnce(radio) {
    if (!radio || radio.__jmtRadioBound) return;
    radio.__jmtRadioBound = true;

    radio.addEventListener('change', function (event) {
      if (!event.isTrusted || APPLYING || !radio.checked) return;
      preserveLandingView();

      if (!window.JMT_PREF) return;

      var type = inferRadioType(radio);
      if (!type) return;

      var v = radio.value;
      if (isPlaceholderValue(v)) return;

      if (type === 'cut') {
        setActiveExperimentalCut(isStandardCut(v) ? '' : v);
        setCapturedPref({ cut: v });
      }
      if (type === 'size') setCapturedPref({ size: v });
      if (type === 'thongStrapStyle') setCapturedPref({ thongStrapStyle: v });
    });
  }

  function bindAllCapture() {
    var selects = getVariantSelects();
    for (var i = 0; i < selects.length; i++) bindCaptureOnce(selects[i]);

    var radios = getVariantRadios();
    for (var j = 0; j < radios.length; j++) bindRadioCaptureOnce(radios[j]);
  }

  function scheduleRebindAndApply() {
    if (REBIND_TIMER) return;
    REBIND_TIMER = window.setTimeout(function () {
      REBIND_TIMER = null;

      bindAllCapture();

      if (window.JMT_PREF) {
        var pref = window.JMT_PREF.get();
        if (pref) applyPrefToPDP(pref);
      } else {
        commitCurrentSitewideControlsIfNeeded();
      }
    }, 80);
  }

  function observeRerenders() {
    if (OBSERVING) return;

    var root = document.querySelector('main') || document.body;
    if (!root) return;

    try {
      var mo = new MutationObserver(function () {
        // Debounced to avoid thrash / loops
        scheduleRebindAndApply();
      });

      mo.observe(root, { subtree: true, childList: true });
      OBSERVING = true;
    } catch (e) {}
  }

  function commitCurrentSitewideControlsIfNeeded() {
    var groups = groupVariantRadios();

    for (var i = 0; i < groups.length; i++) {
      var group = groups[i];
      if (!group.length) continue;

      var sharedSelect = getSitewideSelectForRadio(group[0]);
      if (!sharedSelect || !sharedSelect.value) continue;

      var checked = group.find(function (radio) { return radio.checked; });
      if (checked && String(checked.value) === String(sharedSelect.value)) continue;

      sharedSelect.dispatchEvent(new Event('change', { bubbles: true }));
      return true;
    }

    return false;
  }

  function init() {
    if (STARTED || !window.JMT_PREF) return false;

    STARTED = true;
    PREF_EVT = window.JMT_PREF.EVT || 'jmt:pref:change';
    document.addEventListener('change', rememberExperimentalCut, true);

    // 1) Apply stored pref on load
    var pref = window.JMT_PREF.get();
    if (pref) applyPrefToPDP(pref);

    // 2) Re-apply when pref changes (from collection bar, sticky, etc)
    window.addEventListener(PREF_EVT, function (e) {
      if (e && e.detail) applyPrefToPDP(e.detail, !CAPTURING_PDP && !ACTIVE_EXPERIMENTAL_CUT);
    });

    // 3) Capture PDP user changes → pref
    bindAllCapture();

    // 4) Rebind on dynamic UI rebuilds
    observeRerenders();

    // 5) bfcache restore (back button)
    window.addEventListener('pageshow', function () {
      scheduleRebindAndApply();
    });

    return true;
  }

  function boot() {
    if (init()) return;

    var attempts = 0;
    var timer = window.setInterval(function () {
      attempts += 1;
      if (init() || attempts >= 100) window.clearInterval(timer);
    }, 50);
  }

  ready(function () {
    // Button → shared-controller routing must work even when the preference
    // engine arrives late or is intentionally omitted from a landing page.
    bindAllCapture();
    observeRerenders();

    [120, 600, 1400].forEach(function (delay) {
      window.setTimeout(function () {
        commitCurrentSitewideControlsIfNeeded();
      }, delay);
    });

    boot();
  });

})();
